<template>
  <div>
    <b-modal
      id="editClassAdvisorCombination"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-keyboard
      no-close-on-esc
      size="xl"
      centered
    >
      <b-overlay
        :show="loadTeachers"
        spinner-variant="primary"
        spinner-type="grow"
        spinner-small
        rounded="md"
      >
        <div class="loading" v-if="fetchData">
          <load-data></load-data>
        </div>

        <div v-else>
          <!-- Existing combinations -->
          <div
            class="existingCombinations"
            v-if="existingClassAdvisorCombinations.length != 0"
          >
            <h5>Existing Class Advisor Combinations</h5>
            <div class="courseTeachers d-flex flex-wrap">
              <p
                v-for="(advisor, index) in existingClassAdvisorCombinations"
                :key="index"
                class="bg-info p-2 text-white rounded ml-2"
                style="font-size: 12px"
              >
                {{ advisor.subgroup.name + ' - ' + advisor.teacher.first_name }}
                <span
                  class="text-danger btn m-0 p-0"
                  @click="deleteClassAdvisorCombination(advisor.id, index)"
                >
                  <i class="fas fa-trash ml-2"></i>
                </span>
              </p>
            </div>
          </div>

          <div class="resetTeachers text-center" v-if="subgroupAdvisors.length != 0">
            <b-button variant="danger" @click="resetAdvisors()" size="sm">Clear all</b-button>
          </div>

          <!-- Selection area -->
          <div class="d-flex mt-3" style="height: 200px">
            <div class="subgroupNames w-50" style="overflow-y: scroll">
              <b-form-checkbox
                v-for="(subgroup, index) in subgroupsTeachersOptions"
                :key="index"
                v-model="selectedSubgroupAdvisors"
                :value="subgroup.value"
                :disabled="subgroup.disabled"
                @change="onSubgroupChange(subgroup.value)"
                size="sm"
              >
                {{ subgroup.text }}
              </b-form-checkbox>
            </div>

            <div
              class="subgroupTeachers w-50"
              v-if="subgroupAdvisors.length != 0"
              style="overflow-y: scroll"
            >
              <div class="users d-flex flex-wrap">
                <p
                  v-for="(advisor, index) in subgroupAdvisors"
                  :key="index"
                  class="bg-success p-2 text-white rounded ml-2"
                  style="font-size: 12px"
                >
                  {{ advisor.subgroup + ' - ' + advisor.advisor }}
                  <span
                    class="text-white btn m-0 p-0"
                    @click="subgroupAdvisors.splice(index, 1)"
                  >
                    <i class="far fa-times-circle ml-2"></i>
                  </span>
                </p>
              </div>
            </div>
          </div>

          <hr /><br />

          <!-- Action buttons -->
          <div class="submitCourseTeacherCombination d-flex justify-content-between">
            <b-button
              variant="primary"
              @click="prepareSyncAdvisorsPostData()"
              size="md"
              :disabled="submitBtn.disabled || selectedSubgroupAdvisors.length == 0"
            >
              {{ submitBtn.text }}
            </b-button>

            <b-button
              variant="danger"
              size="md"
              @click="cancel()"
              :disabled="submitBtn.disabled"
            >
              Cancel
            </b-button>
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
export default {
  props: ["subgroupsForTeachers", "surveyId"],

  async created() {
    await this.resetAdvisors();
  },

  data() {
    return {
      fetchData: false,
      loadTeachers: false,
      subgroupsTeachersOptions: this.subgroupsForTeachers,
      selectedSubgroupAdvisors: [],
      subgroupAdvisors: [],
      existingClassAdvisorCombinations: [],
      submitBtn: {
        text: "Submit",
        disabled: false,
      },
    };
  },

  methods: {
    // ✅ Open modal and fetch existing data
    async checkClassAdvisorCombination() {
      this.$bvModal.show("editClassAdvisorCombination");
      await this.getClassAdvisorCombination();
    },

    // ✅ Fetch existing combinations
    async getClassAdvisorCombination() {
      this.fetchData = true;
      const url =
        this.$store.getters.getAPIKey.studentFeedbackSurvey +
        `/${this.surveyId}/studfbsurveyclassadvisors`;

      try {
        const response = await this.$axios.get(url);
        this.existingClassAdvisorCombinations = response.data;

        // Extract all subgroup_ids to pre-check checkboxes
        this.selectedSubgroupAdvisors = this.existingClassAdvisorCombinations
          .map(item => item.subgroup_id)
          .filter((id, index, self) => id != null && self.indexOf(id) === index);

        this.fetchData = false;
      } catch {
        this.$toast.error("Something went wrong!", {
          position: "top",
          duration: 3000,
        });
        this.cancel();
        this.fetchData = false;
      }
    },

    // ✅ Reset all states
    async resetAdvisors() {
      this.subgroupsTeachersOptions = this.subgroupsForTeachers;
      this.subgroupsTeachersOptions.forEach((subgroup) => (subgroup.disabled = false));
      this.selectedSubgroupAdvisors = [];
      this.subgroupAdvisors = [];
    },

    // ✅ Handle checkbox change
    async onSubgroupChange(subgroupId) {
      const isChecked = this.selectedSubgroupAdvisors.includes(subgroupId);

      if (isChecked) {
        // Newly checked → fetch its advisors
        await this.getSubgroupAdvisors(subgroupId);
      } else {
        // Unchecked → remove saved combinations
        const toDelete = this.existingClassAdvisorCombinations.filter(
          (item) => item.subgroup_id === subgroupId
        );

        for (const advisor of toDelete) {
          const index = this.existingClassAdvisorCombinations.findIndex(
            (item) => item.id === advisor.id
          );
          if (index !== -1) {
            await this.deleteClassAdvisorCombination(advisor.id, index);
          }
        }

        // Remove any newly added (not saved) advisors
        this.subgroupAdvisors = this.subgroupAdvisors.filter(
          (item) => item.subgroup_id !== subgroupId
        );

        // Re-enable checkbox
        const optionIndex = this.subgroupsTeachersOptions.findIndex(
          (opt) => opt.value === subgroupId
        );
        if (optionIndex !== -1) this.subgroupsTeachersOptions[optionIndex].disabled = false;
      }
    },

    // ✅ Load advisors for a subgroup
    async getSubgroupAdvisors(subgroupId) {
      this.loadTeachers = true;

      const selectedIndex = this.selectedSubgroupAdvisors.findIndex(
        (val) => val === subgroupId
      );
      const optionIndex = this.subgroupsTeachersOptions.findIndex(
        (opt) => opt.value === subgroupId
      );

      const url = `${this.$store.getters.getAPIKey.mainAPI}/usersubgroup/${subgroupId}/withadvisors`;

      try {
        const response = await this.$axios.get(url);
        const subgroupUsers = response.data;

        if (!subgroupUsers.institution_users || subgroupUsers.institution_users.length === 0) {
          this.$toast.warning("No Class Advisors found!", {
            position: "top",
            duration: 3000,
          });
          this.selectedSubgroupAdvisors.splice(selectedIndex, 1);
        } else {
          this.subgroupsTeachersOptions[optionIndex].disabled = true;

          subgroupUsers.institution_users.forEach((advisor) => {
            const existsInExisting = this.existingClassAdvisorCombinations.some(
              (item) =>
                item.subgroup.id === subgroupUsers.id &&
                item.teacher.first_name === advisor.first_name
            );

            const existsInCurrent = this.subgroupAdvisors.some(
              (item) =>
                item.subgroup_id === subgroupUsers.id &&
                item.advisor === advisor.first_name
            );

            if (!existsInExisting && !existsInCurrent) {
              this.subgroupAdvisors.push({
                subgroup: subgroupUsers.name,
                subgroup_id: subgroupUsers.id,
                advisor: advisor.first_name,
                institution_user_id: advisor.id,
              });
            }
          });
        }
      } catch {
        this.$toast.error("Failed to load Class Advisors", {
          position: "top",
          duration: 3000,
        });
      }

      this.loadTeachers = false;
    },

    // ✅ Sync advisors with backend
    async prepareSyncAdvisorsPostData() {
      this.submitBtn.text = "Please wait ...";
      this.submitBtn.disabled = true;

      const syncStatus = await this.syncAdvisorsWithFeedbackSurvey();

      if (syncStatus) {
        this.$toast.success("Linking Class Advisors successfully done.", {
          position: "top",
          duration: 3000,
        });
        this.cancel();
      } else {
        this.$toast.error("Linking Class Advisors failed. Please try again.", {
          position: "top",
          duration: 3000,
        });
        this.cancel();
      }
    },

    async syncAdvisorsWithFeedbackSurvey() {
      const syncUrl = this.$store.getters.getAPIKey.studentFeedbackSurveySyncAdvisors;
      const url = syncUrl.replace("survey_id", this.surveyId);
      let status = null;

      await this.$axios
        .post(url, { subgroup_advisors: this.subgroupAdvisors })
        .then(() => (status = true))
        .catch(() => (status = false));

      return status;
    },

    // ✅ Delete saved advisor combination
    async deleteClassAdvisorCombination(advisorId, index) {
      this.loadTeachers = true;
      const url =
        this.$store.getters.getAPIKey.studentFeedbackSurvey +
        `/studfbsurveyclassadvisor/${advisorId}`;

      await this.$axios
        .delete(url)
        .then(() => {
          this.$toast.success("Class Advisor combination removed.", {
            position: "top",
            duration: 3000,
          });
          this.existingClassAdvisorCombinations.splice(index, 1);
        })
        .catch(() => {
          this.$toast.error("Something went wrong, Please try again!", {
            position: "top",
            duration: 3000,
          });
        });

      this.loadTeachers = false;
    },

    //  Cancel modal
    async cancel() {
      this.$bvModal.hide("editClassAdvisorCombination");
      this.submitBtn.text = "Submit";
      this.submitBtn.disabled = false;
      await this.resetAdvisors();
    },
  },
};
</script>
